import { useEffect, useMemo, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAtomValue } from 'jotai'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useCreateContactBankAccount } from 'hooks/contacts/useCreateContactBankAccount'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import {
  BaseErrorResponse,
  BeneficiaryDetailsCurrency,
  BeneficiaryEntityType,
  BeneficiaryTransferDestination,
  USDBeneficiaryOptions,
} from 'types/beneficiaries'
import { CreateContactBankAccountInstrumentProps } from 'types/contacts'
import { contactAtom } from '../atoms/contactAtoms'
import { DEFAULT_FIAT_INSTRUMENT } from '../constants'
import {
  ContactFiatInstrumentFormData,
  contactFiatInstrumentSchema,
} from '../schemas/contactFiatInstrumentSchema'
import { DEFAULT_CUBIX_BANK_CODE } from '../schemas/fiatBankAccountValidators'
import { FormFieldLabels, Step, Steps } from '../types/fiatInstruments'

export const useFiatInstrumentForm = () => {
  const { t } = useTranslation()

  const contact = useAtomValue(contactAtom)

  const [step, setStep] = useState<Step>(Steps.TRANSFER_TYPE)

  const { createBankAccount, isSubmittingBankAccount, submit2Fa, isSubmitting2Fa } =
    useCreateContactBankAccount({
      onSubmitBankAccountSuccess: (response: BaseErrorResponse) => {
        setStep(Steps.CONFIRMATION)
        setValue('challengeCode', response?.data?.challenge?.value)
        setValue('callbackUrl', response?.data?.callbackUrl)
      },
      onSubmitBankAccountError: _error => {},
      onSubmit2FASuccess: () => {},
      onSubmit2FAError: _error => {},
    })

  const formSchema = contactFiatInstrumentSchema(t)

  const form = useForm<ContactFiatInstrumentFormData>({
    defaultValues: DEFAULT_FIAT_INSTRUMENT,
    resolver: yupResolver(formSchema),
  })

  const { control, formState, handleSubmit, clearErrors, resetField, setValue, register, trigger } =
    form

  const formValues = useWatch({ control }) as ContactFiatInstrumentFormData

  useEffect(() => {
    if (formValues?.paymentMethod === USDBeneficiaryOptions.CUBIX) {
      setValue('bankCode', DEFAULT_CUBIX_BANK_CODE)
      return
    }

    setValue('bankCode', '')
  }, [formValues?.paymentMethod])

  const isInternationalBankAccount =
    formValues?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
  const isBusinessBankAccount = formValues?.entityType === BeneficiaryEntityType.BUSINESS

  const formFieldLabels: FormFieldLabels = useMemo(() => {
    if (formValues?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL) {
      switch (formValues?.currency) {
        case BeneficiaryDetailsCurrency.EUR:
        case BeneficiaryDetailsCurrency.GBP:
          return {
            accountNumber: t('formFields.iban'),
            accountNumberPlaceholder: t('formFields.placeholder.iban'),
            bankCode: t('formFields.bic'),
            bankCodePlaceholder: t('formFields.placeholder.bic'),
          }
        case BeneficiaryDetailsCurrency.USD:
          return {
            accountNumber: t('formFields.accountNumberOrIban'),
            accountNumberPlaceholder: t('formFields.placeholder.accountNumberOrIban'),
            bankCode: t('formFields.swiftCode'),
            bankCodePlaceholder: t('formFields.placeholder.swiftCode'),
          }
        default:
          return {
            accountNumber: t('formFields.accountNumber'),
            accountNumberPlaceholder: t('formFields.placeholder.accountNumber'),
            bankCode: t('formFields.bankCode'),
            bankCodePlaceholder: t('formFields.placeholder.bankCode'),
          }
      }
    }

    switch (formValues?.currency) {
      case BeneficiaryDetailsCurrency.EUR:
        return {
          accountNumber: t('formFields.iban'),
          accountNumberPlaceholder: t('formFields.placeholder.iban'),
          bankCode: t('formFields.bic'),
          bankCodePlaceholder: t('formFields.placeholder.bic'),
        }
      case BeneficiaryDetailsCurrency.GBP:
        return {
          accountNumber: t('formFields.accountNumber'),
          accountNumberPlaceholder: t('formFields.placeholder.accountNumber'),
          bankCode: t('formFields.sortCode'),
          bankCodePlaceholder: t('formFields.placeholder.sortCode'),
        }
      case BeneficiaryDetailsCurrency.USD:
        if (formValues?.paymentMethod === USDBeneficiaryOptions.CUBIX) {
          return {
            accountNumber: t('formFields.cubixAccountNumber'),
            accountNumberPlaceholder: t('formFields.placeholder.cubixAccountNumber'),
            bankCode: t('formFields.bankCode'),
            bankCodePlaceholder: t('formFields.placeholder.bankCode'),
          }
        }
        return {
          accountNumber: t('formFields.accountNumber'),
          accountNumberPlaceholder: t('formFields.placeholder.accountNumber'),
          bankCode: t('formFields.routingNumber'),
          bankCodePlaceholder: t('formFields.placeholder.routingNumber'),
        }
      default:
        return {
          accountNumber: t('formFields.accountNumber'),
          accountNumberPlaceholder: t('formFields.placeholder.accountNumber'),
          bankCode: t('formFields.bankCode'),
          bankCodePlaceholder: t('formFields.placeholder.bankCode'),
        }
    }
  }, [formValues?.currency, formValues?.transferDestination, formValues?.paymentMethod])

  const onSubmit: SubmitHandler<typeof formValues> = () => {
    const fiatInstrumentPayload: CreateContactBankAccountInstrumentProps = {
      contactId: contact?.id,
      request: {
        currency: formValues?.currency,
        description: formValues?.description,
        // bankTypeIdentifier: string
        destination: formValues?.transferDestination as BeneficiaryTransferDestination,
        accountNumber: formValues?.accountNumber,
        bankCode: formValues?.bankCode,
        // bank: ContactBankAccountBank
        // intermediaryBank: IntermediaryBank
        type: 'BANK_ACCOUNT',
      },
    }

    createBankAccount(fiatInstrumentPayload)
  }

  return {
    // steps
    step,
    setStep,
    // form
    formValues,
    formState,
    isInternationalBankAccount,
    isBusinessBankAccount,
    formFieldLabels,
    errors: formState.errors,
    clearErrors,
    resetField,
    setValue,
    register,
    // form submit
    isSubmittingBankAccount,
    isSubmitting2Fa,
    handleSubmit,
    onSubmit,
    submit2Fa,
    trigger,
  }
}
